<template>
  <div :class="`w-full h-full p-0 border-gray-100 ${classes}`">
    <div :name="name" :class="[`${disabled || readOnly ? 'hide-border' : ''}`]" class="relative w-full h-full p-0">
      <div
        ref="editor"
        :class="[editorClasses, `${disabled || readOnly ? 'hide-border' : 'light-border'}`]"
        class="text-editor-headers overflow-visible">
        <div v-show="errorMessage" class="absolute inset-y-0 right-0 z-10 flex items-center pr-3 pointer-events-none">
          <CircleX class="w-5 h-5 text-red-400" />
        </div>
        <div
          :class="[meta.valid && meta.dirty && !meta.pending ? 'opacity-100' : 'opacity-0']"
          class="absolute inset-y-0 right-0 z-10 flex items-center pr-3 transition-all duration-500 pointer-events-none">
          <CheckCircle class="w-5 h-5 text-green-400" />
        </div>
        <p class="absolute right-0 text-red-400 first-letter:capitalize top-10 text-xsm" :class="[errorMessage ? 'visible' : 'invisible']">
          {{ errorMessage }}
        </p>
      </div>

      <label
        v-show="showLabel"
        :class="[
          errorMessage ? 'peer-placeholder-shown:text-status-error-red' : '',
          focus || inputValue ? '-top-3 dark:text-dark-body-text ' : ''
        ]"
        :for="name"
        class="absolute left-1 rounded-md px-1 -top-0 text-gray-400 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-2 peer-focus:-top-3.5 text-sm font-medium peer-focus:font-normal peer-focus:text-blue bg-light-label-background dark:bg-dark-box-background">{{ label }}</label>
    </div>
  </div>
</template>

<script>
import Quill from "quill"
import "quill/dist/quill.snow.css"
import "quill/dist/quill.bubble.css"
import { useField } from "vee-validate"
import { ref, watch, onMounted } from "vue"
import { CircleX, CheckCircle } from "lucide-vue-next"
import { Mention as QuillMention, MentionBlot } from "quill-mention"
import htmlEditButton from "quill-html-edit-button"
import { useStore } from "vuex"
import Delta from "quill-delta"
import { utils } from "@/store/helpers"
import { toRef } from "vue"

Quill.register({
  "blots/mention": MentionBlot,
  "modules/mention": QuillMention,
  delta: Delta,
  "modules/htmlEditButton": htmlEditButton
})

export default {
  components: {
    CircleX,
    CheckCircle
  },
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    name: {
      type: String,
      default: "description"
    },
    label: {
      type: String,
      required: false
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    value: {
      type: String,
      default: ""
    },
    contentType: {
      type: String,
      default: "html" // Supports "html" or "text" or delta
    },
    theme: {
      type: String,
      default: "snow" // Supports "snow" or "bubble"
    },
    toolbar: {
      type: String,
      default: "essential" // Essential CRM features with color support
    },
    classes: {
      type: String,
      default: ""
    },
    inputClasses: {
      type: String,
      default: ""
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    noBorder: {
      type: Boolean,
      default: false
    },
    transparentBg: {
      type: Boolean,
      default: false
    },
    preserveTextColor: {
      type: Boolean,
      default: false
    },
    validateOnInput: {
      type: Boolean,
      default: false
    },
    mentionItems: {
      type: Array,
      default: null
    },
    isEmailText: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "update:content", "changed"],
  setup(props, { emit }) {
    const name = toRef(props, "name")
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      meta,
      handleChange
    } = useField(name, undefined, {
      initialValue: props.value || props.modelValue,
      validateOnValueUpdate: props.validateOnInput
    })

    const editor = ref(null) // Reference to Quill editor container
    const quillInstance = ref(null) // Store Quill instance
    const focus = ref(false)
    const store = useStore()

    const internalUpdateInProgress = ref(false)
    const getToolbarOptions = () => {
      // Essential CRM Toolbar
      const toolbarOptions = {
        essential: [
          [{ header: [1, 2, 3, 4, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }, { align: [] }],
          [{ color: [] }, { background: [] }],
          ["blockquote", "code-block", "link"],
          [{ color: [] }, "clean"],
          ["clean"]
        ],
        minimal: [
          [{ header: 1 }, { header: 2 }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }, { align: [] }],
          ["clean"]
        ],
        full: [
          ["bold", "italic", "underline", "strike"], // Basic text styles
          ["blockquote", "code-block"], // Block styles
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }, { list: "check" }], // Ordered, bullet, and check lists
          [{ script: "sub" }, { script: "super" }], // Subscript/Superscript
          [{ indent: "-1" }, { indent: "+1" }], // Indent (outdent and indent options)

          [{ size: ["10px", "12px", "16px", "18px", "24px", "32px"] }], // Font size options
          [{ header: [1, 2, 3, 4, 5, 6, false] }], // Additional header levels
          [{ font: [] }],
          [{ align: [] }], // Text alignment options (left, center, right, justify)
          [{ lineheight: ["1", "1.5", "2", "2.5", "3"] }], // Line height options

          ["link", "image"], // Embedding options for links, videos, images, and formulas

          ["clean"], // Clear formatting
          [{ insertHorizontalRule: [] }] // Horizontal line (requires custom module)
        ]
      }
      return toolbarOptions[props.toolbar] || toolbarOptions.full
    }
    const emailMentions = [
      { id: "FirstName", value: "FirstName", label: "FirstName" },
      { id: "LastName", value: "LastName", label: "LastName" },
      { id: "FirstNameInitial", value: "FirstNameInitial", label: "FirstNameInitial" },
      { id: "LastNameInitial", value: "LastNameInitial", label: "LastNameInitial" },
      { id: "Email", value: "Email", label: "Email" },
      { id: "Name", value: "Name", label: "Name" }
    ]
    const allMentions = props.isEmailText ? emailMentions : []
    if (props.mentionItems) {
      allMentions.push(
        ...props.mentionItems.map((item) => ({ denotationChar: "", id: item?.value, value: item?.value, label: item?.label }))
      )
    }
    // by default we search store.state.users AND IF props.mentionItems is passed we search that too
    const searchMentionItems = async (searchTerm) => {
      const values = allMentions.concat(store.state.data.users.map((user) => ({ id: user.id, value: user.id, label: user.full_name })))
      return utils.scoreSearch(searchTerm, values, ["value", "label"])
    }

    const getModules = () => {
      let modules = {
        toolbar: getToolbarOptions(),
        mention: {
          allowedChars: /^[A-Za-z\s]*$/,
          mentionDenotationChars: ["@"],
          source: async function (searchTerm, renderList) {
            const matchedItems = await searchMentionItems(searchTerm)
            renderList(matchedItems)
          },
          renderItem(item) {
            return `${item.label || item.value}`
          }
        },
        htmlEditButton: {}
      }
      if (props.contentType === "delta") {
        modules["delta"] = Delta
      }
      if (props.disabled) {
        modules["toolbar"] = []
      }

      return modules
    }

    onMounted(() => {
      // Initialize Quill editor with modules including mention module
      let configs = {
        theme: props.theme,
        readOnly: props.readOnly || props.disabled,
        placeholder: props.placeholder,
        modules: getModules()
      }
      if (props.disabled) {
        delete configs.theme
      }

      // Create Quill instance targeting the DOM element referenced by editor
      quillInstance.value = new Quill(editor.value, {
        theme: props.theme,
        readOnly: props.readOnly || props.disabled,
        placeholder: props.placeholder,
        modules: getModules()
      })

      // Set initial content based on contentType
      const initialContent = props.modelValue || props.value || ""

      // Update vee-validate field with initial content
      if (initialContent) {
        inputValue.value = initialContent
      }

      if (initialContent) {
        if (props.contentType === "text") {
          quillInstance.value.setText(initialContent)
        } else if (props.contentType === "delta" && typeof initialContent === "object") {
          quillInstance.value.setContents(initialContent)
        } else {
          // For HTML content, use innerHTML - safer approach
          quillInstance.value.root.innerHTML = initialContent
        }
      }

      if (props.disabled) {
        const toolbar = document.querySelector(".ql-toolbar")
        const editor = document.querySelector(".ql-editor")
        if (toolbar) {
          toolbar.style.display = "none"
        }
        if (editor) {
          editor.classList.add("text-editor-headers")
        }
      }

      quillInstance.value.on("text-change", () => {
        // Logic moved from 'blur' handler
        let content
        if (props.contentType === "text") {
          content = quillInstance.value.getText().trim() // Extract plain text content
        } else if (props.contentType === "delta") {
          content = quillInstance.value.getContents() // Get Delta object
        } else {
          content = quillInstance.value.root.innerHTML // Default to HTML content
        }

        // Update the `inputValue` from vee-validate first
        // This ensures the Form component gets the updated value
        inputValue.value = content

        // Emit v-model update

        internalUpdateInProgress.value = true // Set flag before emitting
        emit("update:modelValue", content)
        emit("update:content", content)
        emit("changed", content)

        // Always trigger validation to ensure form state is updated
        handleChange(content)

        // Original call from selection-change
        handleBlur() // Trigger vee-validate blur when selection is lost
      })

      // Handle focus and blur events for validation and UI updates
      quillInstance.value.on("selection-change", (range, oldRange, _) => {
        if (range === null && oldRange !== null) {
          // Logic moved from 'blur' handler
          let content
          if (props.contentType === "text") {
            content = quillInstance.value.getText().trim() // Extract plain text content
          } else if (props.contentType === "delta") {
            content = quillInstance.value.getContents() // Get Delta object
          } else {
            content = quillInstance.value.root.innerHTML // Default to HTML content
          }

          // Update the `inputValue` from vee-validate first
          // This ensures the Form component gets the updated value
          inputValue.value = content

          // Emit v-model update

          internalUpdateInProgress.value = true // Set flag before emitting
          emit("update:modelValue", content)
          emit("update:content", content)
          emit("changed", content)

          // Always trigger validation to ensure form state is updated
          handleChange(content)

          // Original call from selection-change
          handleBlur() // Trigger vee-validate blur when selection is lost
        }
      })
    })

    // Watch for external changes to model value or form field value
    watch(
      [() => props.modelValue, () => props.value, inputValue],
      ([newModelValue, newPropValue, newInputValue], _, onCleanup) => {
        if (internalUpdateInProgress.value) {
          internalUpdateInProgress.value = false // Reset the flag
          return // Skip the rest of the watcher logic for this update
        }

        // Prevent update loops with a flag
        const preventLoop = ref(false)
        onCleanup(() => {
          preventLoop.value = false
        })

        if (preventLoop.value) {
          return
        }
        preventLoop.value = true

        // Use setTimeout to ensure this runs after the current call stack
        setTimeout(() => {
          preventLoop.value = false
        }, 0)
        if (!quillInstance.value) {
          return
        }

        // Determine which value to use (prioritize model value)
        const newValue = newModelValue || newPropValue || newInputValue

        // Only update if we have a new value and it's different from current content
        if (newValue || newValue === "") {
          let needsUpdate = false
          if (props.contentType === "text") {
            const currentText = quillInstance.value.getText().trim()
            needsUpdate = currentText !== (newValue || "").trim()
          } else if (props.contentType === "delta" && typeof newValue === "object") {
            // For delta objects we need to check structure
            needsUpdate = true // Simplified - we could do deeper comparison
          } else {
            // For HTML content
            const currentHtml = quillInstance.value.root.innerHTML
            needsUpdate = currentHtml !== (newValue || "")
          }

          // Update if needed
          if (needsUpdate) {
            if (props.contentType === "text") {
              quillInstance.value.setText(newValue || "")
            } else if (props.contentType === "delta" && typeof newValue === "object") {
              quillInstance.value.setContents(newValue)
            } else {
              // For HTML content, use innerHTML - safer than dangerouslyPasteHTML
              quillInstance.value.root.innerHTML = newValue || ""
            }
          }
        }
      },
      { deep: true }
    )

    return {
      inputValue,
      errorMessage,
      meta,
      editor,
      focus
    }
  },
  computed: {
    editorClasses() {
      return [
        `${this.transparentBg ? "" : "dark:bg-dark-box-background"} ${
          this.preserveTextColor ? "" : "dark:text-white"
        } w-full ${this.inputClasses} ${this.noBorder ? "border-0 shadow-none" : ""} ${
          this.errorMessage ? "border-status-error-red text-status-error-red" : ""
        } text-editor-headers`
      ]
    }
  }
}
</script>
<style lang="scss">
/* Placeholder styling */
.placeholder {
  color: red; /* Chrome, Firefox, Opera, Safari 10.1+ */
  opacity: 1; /* Firefox */
}

/* Make selected text bold globally in the editor */
.ql-editor ::selection {
  font-weight: bold;
  background-color: rgba(13, 138, 186, 0.3); /* Light blue background color with opacity */
  color: inherit;
}
.light-border {
  border: 1px solid #e0e0e0 !important;
}
.hide-border {
  .ql-toolbar {
    display: none !important;
  }
}

/* Text Editor Header and Font Styles */
.text-editor-headers {
  border-top: none !important;
  font-size: 1rem !important; // Default font size

  /* Make selected text bold */
  *::selection {
    font-weight: bold;
    background-color: rgba(13, 138, 186, 0.3); /* Light blue background color with opacity */
    color: inherit;
  }

  /* Header Sizes */
  .ql-header-1,
  h1 {
    font-size: 4em !important;
  }
  .ql-header-2,
  h2 {
    font-size: 3.5em !important;
  }
  .ql-header-3,
  h3 {
    font-size: 3em !important;
  }
  .ql-header-4,
  h4 {
    font-size: 2.5em !important;
  }
  .ql-header-5,
  h5 {
    font-size: 2em !important;
  }
  .ql-header-6,
  h6 {
    font-size: 1.5em !important;
  }

  /* Font Families */
  .ql-font-roboto {
    font-family: "Roboto", sans-serif;
  }
  .ql-font-sans-serif {
    font-family: sans-serif;
  }
  .ql-font-serif {
    font-family: serif;
  }
  .ql-font-monospace {
    font-family: monospace;
  }
  .ql-font-times-new-roman {
    font-family: "Times New Roman", Times, serif;
  }
  .ql-font-arial {
    font-family: Arial, sans-serif;
  }
  .ql-font-courier-new {
    font-family: "Courier New", Courier, monospace;
  }
  .ql-font-open-sans {
    font-family: "Open Sans", sans-serif;
  }
  .ql-size-20px {
    font-size: 20px;
  }
  .ql-size-24px {
    font-size: 24px;
  }
  .ql-size-32px {
    font-size: 32px;
  }
  .ql-size-36px {
    font-size: 36px;
  }
  .ql-size-48px {
    font-size: 48px;
  }
  .ql-size-64px {
    font-size: 64px;
  }
}

/* Tooltip Styling */
.ql-tooltip.ql-hidden > input[type="text"],
.ql-tooltip-editor,
.ql-tooltip-arrow {
  display: none !important;
}

.ql-container.ql-snow {
  border: none;
}
.ql-toolbar.ql-snow {
  border-bottom: none;
}
/* Toolbar and Container Styling */

/* Mention List Styling */
.ql-mention-list {
  background-color: #fff !important;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); /* shadow */
  padding: 5px;
  border-radius: 5px;
  color: gray;
  cursor: pointer;
}

/* Mention List Container */
.ql-mention-list-container {
  position: absolute;
  visibility: visible;
  top: 34px;
  left: 15px;
  width: auto;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 8px 0;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Mention List Items */
.ql-mention-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ql-mention-list-item {
  padding: 10px 16px;
  cursor: pointer;
  color: #333;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
}

.ql-mention-list-item:hover {
  background-color: #f5f5f5;
}

.ql-mention-list-item.selected {
  background-color: var(--selected-item-color);
  color: #ffffff;
  font-weight: bold;
}

/* Custom Scrollbar for Mention List Container */
.ql-mention-list-container::-webkit-scrollbar {
  width: 8px;
}

.ql-mention-list-container::-webkit-scrollbar-thumb {
  background-color: #cccccc;
  border-radius: 4px;
}

/* Font Styling in Dropdown */
.ql-picker.ql-font .ql-picker-item[data-value="roboto"] {
  font-family: "Roboto", sans-serif;
}

.ql-picker.ql-font .ql-picker-item[data-value="sans-serif"] {
  font-family: sans-serif;
}

.ql-picker.ql-font .ql-picker-item[data-value="serif"] {
  font-family: serif;
}

.ql-picker.ql-font .ql-picker-item[data-value="monospace"] {
  font-family: monospace;
}

.ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"] {
  font-family: "Times New Roman", Times, serif;
}

.ql-picker.ql-font .ql-picker-item[data-value="arial"] {
  font-family: Arial, sans-serif;
}

.ql-picker.ql-font .ql-picker-item[data-value="courier-new"] {
  font-family: "Courier New", Courier, monospace;
}

.ql-picker.ql-font .ql-picker-item[data-value="open-sans"] {
  font-family: "Open Sans", sans-serif;
}
/* Optional: Preview custom sizes in dropdown */
.ql-picker.ql-size .ql-picker-item[data-value="20px"] {
  font-size: 20px;
}

.ql-picker.ql-size .ql-picker-item[data-value="24px"] {
  font-size: 24px;
}

.ql-picker.ql-size .ql-picker-item[data-value="32px"] {
  font-size: 32px;
}

.ql-picker.ql-size .ql-picker-item[data-value="36px"] {
  font-size: 36px;
}

.ql-picker.ql-size .ql-picker-item[data-value="48px"] {
  font-size: 48px;
}

.ql-picker.ql-size .ql-picker-item[data-value="64px"] {
  font-size: 64px;
}
</style>
